import { useMemo, useRef } from 'react';
import c from 'classnames';

import { Typography } from 'components';

import { ResourcesEmptyState } from '../../ResourcesEmptyState/ResourcesEmptyState';
import { useVisibleVideoResources } from '../../hooks/useVisibleVideoResources';
import { useWebinarInteraction } from '../../hooks/useWebinarInteraction';
import { useYouTubePlayer } from '../../hooks/useYouTubePlayer';
import {
  getYouTubeVideoId,
  isExclusiveLocked,
  matchesFilters,
  ResourceFilters,
} from '../VideoResources.helpers';
import { FeaturedVideo } from './FeaturedVideo';
import { VideoCard } from './VideoCard';
import UpgradeModal from '../UpsellModal/UpgradeModal';

import styles from './Webinars.module.css';

interface WebinarsProps {
  filters: ResourceFilters;
}

// The "Past Webinars" experience: featured player, side list, locked/upsell
// states, and the empty state. Owns its own data and interaction so the parent
// section only has to render it.
export const Webinars = ({ filters }: WebinarsProps) => {
  const { visibleResources, isAnyTinyPlus } = useVisibleVideoResources();

  const filteredResources = useMemo(
    () => visibleResources?.filter(video => matchesFilters(video, filters)),
    [visibleResources, filters],
  );

  const exclusiveVideos = useMemo(
    () => visibleResources?.filter(video => video.is_tiny_plus_only) ?? [],
    [visibleResources],
  );

  const {
    selectedVideo,
    showUpgradeModal,
    upsellTriggerVideoId,
    closeUpgradeModal,
    handleCardSelect,
  } = useWebinarInteraction({ filteredResources, isAnyTinyPlus });

  const playerHostRef = useRef<HTMLDivElement>(null);
  const isSelectedLocked =
    !!selectedVideo && isExclusiveLocked(selectedVideo, isAnyTinyPlus);

  useYouTubePlayer({
    videoId:
      selectedVideo && !isSelectedLocked
        ? getYouTubeVideoId(selectedVideo.video_url) || null
        : null,
    hostRef: playerHostRef,
    mute: !!selectedVideo?.is_featured,
  });

  const videoList = filteredResources?.filter(
    video => video.id !== selectedVideo?.id,
  );
  const isFiltering = filters.tags.length > 0 || filters.tinyPlusOnly;
  const isFilteredEmpty = isFiltering && filteredResources?.length === 0;

  return (
    <>
      <Typography variant='heading-l'>Past Webinars</Typography>

      <div className={styles.videosContainer}>
        {isFilteredEmpty ? (
          <ResourcesEmptyState />
        ) : (
          <>
            <div className={styles.featuredVideo}>
              <FeaturedVideo
                video={selectedVideo}
                isLocked={isSelectedLocked}
                playerHostRef={playerHostRef}
                onUnlock={() => selectedVideo && handleCardSelect(selectedVideo)}
              />
            </div>
            {/* Mobile-only heading; on desktop the list is a sidebar. */}
            <Typography
              variant='heading-l'
              className={c('hide-on-desktop', styles.moreVideosHeading)}
            >
              More videos
            </Typography>
            <div className={styles.videoListWrapper}>
              <div className={styles.videoList}>
                {videoList?.map(video => (
                  <VideoCard
                    key={video.id}
                    video={video}
                    onSelect={handleCardSelect}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={closeUpgradeModal}
        exclusiveVideos={exclusiveVideos}
        triggerVideoId={upsellTriggerVideoId}
      />
    </>
  );
};
